import { createFileRoute } from "@tanstack/react-router";
import {
  forwardRef,
  useEffect,
  useRef,
  useState,
  type ForwardedRef,
  type ForwardRefExoticComponent,
  type CSSProperties,
  type ReactNode,
  type RefAttributes,
} from "react";
import QRCode from "qrcode";
import jsPDF from "jspdf";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Printer, Download, Eye } from "lucide-react";
import logo from "@/assets/apex-av-new-logo-optimized.webp";
import __apexTheater from "@/assets/Apex_Theater.webp.asset.json";
const bgTheater = __apexTheater.url;
import bgOutdoor from "@/assets/outdoor-av.webp";
import bgSmartHome from "@/assets/smart-home.webp";
import bgAudio from "@/assets/whole-home-audio.webp";
import bgLighting from "@/assets/lighting-showcase-optimized.webp";
import bgShading from "@/assets/shading-showcase-optimized.webp";
import bgNetworking from "@/assets/networking.jpg";
import bgConference from "@/assets/conference-av.jpg";
import bgBrands from "@/assets/brands-lifestyle.webp";
import bgTvMount from "@/assets/tv-mounting.jpg";
import bgHiddenAudio from "@/assets/hidden-audio.jpg";
import bgClimate from "@/assets/climate-control.jpg";
import bgVoice from "@/assets/voice-control.jpg";
import bgWholeVideo from "@/assets/whole-home-video.jpg";
import bgDenver from "@/assets/denver-service-area.jpg";
import bgApexTheater from "@/assets/Apex_Theater.webp.asset.json";
import bgDenverTheater from "@/assets/denver-home-theater-installation.webp.asset.json";
import bgDenverNetwork from "@/assets/denver-home-network-installation.jpg.asset.json";
import bgDenverPrewire from "@/assets/denver-new-construction-prewire.jpg.asset.json";
import bgDenverRetrofit from "@/assets/denver-retrofit-cable-fishing.webp.asset.json";
import bgOutdoorSpeaker from "@/assets/episode-outdoor-speaker.jpg.asset.json";
import bgStarlight from "@/assets/LUM-X20-Starlight.jpg.asset.json";
import bgNetworkRack from "@/assets/network-rack-installation-denver.jpg.asset.json";
import bgSecurityCam from "@/assets/security-camera-lifestyle.jpg.asset.json";

type BgPreset = {
  id: string;
  label: string;
  kind: "gradient" | "image";
  css: string; // background shorthand for the .card-bg layer
  image?: string;
};

const BG_PRESETS: BgPreset[] = [
  {
    id: "midnight-indigo",
    label: "Midnight Indigo",
    kind: "gradient",
    css:
      "radial-gradient(120% 80% at 100% 0%, rgba(120,90,255,0.55), transparent 55%)," +
      "radial-gradient(90% 120% at 0% 100%, rgba(0,180,255,0.35), transparent 60%)," +
      "linear-gradient(135deg, #0b0b18 0%, #14142b 55%, #1a1440 100%)",
  },
  {
    id: "carbon",
    label: "Carbon",
    kind: "gradient",
    css:
      "radial-gradient(100% 80% at 20% 0%, rgba(77,195,255,0.18), transparent 60%)," +
      "linear-gradient(160deg, #050507 0%, #111114 60%, #1c1c22 100%)",
  },
  {
    id: "electric-blue",
    label: "Electric Blue",
    kind: "gradient",
    css:
      "radial-gradient(120% 100% at 0% 0%, rgba(77,195,255,0.55), transparent 55%)," +
      "linear-gradient(135deg, #041233 0%, #0a2a6b 60%, #0f3a99 100%)",
  },
  {
    id: "sunset",
    label: "Sunset",
    kind: "gradient",
    css:
      "radial-gradient(120% 100% at 100% 0%, rgba(255,180,80,0.45), transparent 55%)," +
      "linear-gradient(135deg, #1a0b18 0%, #3a1030 55%, #5c1a2a 100%)",
  },
  {
    id: "emerald",
    label: "Emerald",
    kind: "gradient",
    css:
      "radial-gradient(120% 100% at 0% 100%, rgba(80,255,180,0.35), transparent 60%)," +
      "linear-gradient(135deg, #04180f 0%, #0a3323 60%, #0f5236 100%)",
  },
  {
    id: "royal-plum",
    label: "Royal Plum",
    kind: "gradient",
    css:
      "radial-gradient(120% 100% at 100% 0%, rgba(200,120,255,0.45), transparent 55%)," +
      "linear-gradient(135deg, #1a0724 0%, #2f0a4a 60%, #4a0f75 100%)",
  },
  {
    id: "graphite-gold",
    label: "Graphite Gold",
    kind: "gradient",
    css:
      "radial-gradient(120% 100% at 100% 0%, rgba(255,200,90,0.32), transparent 55%)," +
      "linear-gradient(135deg, #0a0a0d 0%, #1a1a20 55%, #2a2418 100%)",
  },
  {
    id: "arctic",
    label: "Arctic",
    kind: "gradient",
    css:
      "radial-gradient(120% 100% at 0% 0%, rgba(180,220,255,0.45), transparent 55%)," +
      "linear-gradient(135deg, #0a1420 0%, #10263d 55%, #1c4066 100%)",
  },
  {
    id: "crimson",
    label: "Crimson",
    kind: "gradient",
    css:
      "radial-gradient(120% 100% at 100% 0%, rgba(255,90,90,0.45), transparent 55%)," +
      "linear-gradient(135deg, #180505 0%, #3a0d0d 55%, #6b1414 100%)",
  },
  {
    id: "obsidian",
    label: "Obsidian",
    kind: "gradient",
    css:
      "radial-gradient(100% 80% at 50% 0%, rgba(140,140,160,0.18), transparent 60%)," +
      "linear-gradient(180deg, #000000 0%, #0a0a0f 60%, #14141c 100%)",
  },
  {
    id: "solid-black",
    label: "Solid Black",
    kind: "gradient",
    css: "#000000",
  },
  {
    id: "aurora",
    label: "Aurora",
    kind: "gradient",
    css:
      "radial-gradient(90% 80% at 0% 0%, rgba(80,255,180,0.35), transparent 55%)," +
      "radial-gradient(90% 80% at 100% 100%, rgba(120,90,255,0.45), transparent 55%)," +
      "linear-gradient(135deg, #06121a 0%, #0a1c2e 60%, #10243d 100%)",
  },
  {
    id: "copper",
    label: "Copper",
    kind: "gradient",
    css:
      "radial-gradient(120% 100% at 100% 0%, rgba(255,140,80,0.42), transparent 55%)," +
      "linear-gradient(135deg, #180a05 0%, #3a1a0d 55%, #6b3014 100%)",
  },
  {
    id: "mono-light",
    label: "Mono Light",
    kind: "gradient",
    css: "linear-gradient(135deg, #f6f6f8 0%, #e6e7ec 60%, #d6d8e0 100%)",
  },
  { id: "img-theater", label: "Home Theater", kind: "image", image: bgTheater, css: "" },
  { id: "img-outdoor", label: "Outdoor AV", kind: "image", image: bgOutdoor, css: "" },
  { id: "img-smart-home", label: "Smart Home", kind: "image", image: bgSmartHome, css: "" },
  { id: "img-audio", label: "Whole-Home Audio", kind: "image", image: bgAudio, css: "" },
  { id: "img-video", label: "Whole-Home Video", kind: "image", image: bgWholeVideo, css: "" },
  { id: "img-lighting", label: "Lighting", kind: "image", image: bgLighting, css: "" },
  { id: "img-shading", label: "Shading", kind: "image", image: bgShading, css: "" },
  { id: "img-networking", label: "Networking", kind: "image", image: bgNetworking, css: "" },
  { id: "img-conference", label: "Conference", kind: "image", image: bgConference, css: "" },
  { id: "img-tv-mount", label: "TV Mounting", kind: "image", image: bgTvMount, css: "" },
  { id: "img-hidden-audio", label: "Hidden Audio", kind: "image", image: bgHiddenAudio, css: "" },
  { id: "img-climate", label: "Climate", kind: "image", image: bgClimate, css: "" },
  { id: "img-voice", label: "Voice Control", kind: "image", image: bgVoice, css: "" },
  { id: "img-denver", label: "Denver Skyline", kind: "image", image: bgDenver, css: "" },
  { id: "img-brands", label: "Brands Lifestyle", kind: "image", image: bgBrands, css: "" },
  { id: "img-apex-theater", label: "Apex Theater", kind: "image", image: bgApexTheater.url, css: "" },
  { id: "img-denver-theater", label: "Denver Theater", kind: "image", image: bgDenverTheater.url, css: "" },
  { id: "img-denver-network", label: "Denver Network", kind: "image", image: bgDenverNetwork.url, css: "" },
  { id: "img-denver-prewire", label: "Denver Prewire", kind: "image", image: bgDenverPrewire.url, css: "" },
  { id: "img-denver-retrofit", label: "Denver Retrofit", kind: "image", image: bgDenverRetrofit.url, css: "" },
  { id: "img-outdoor-speaker", label: "Outdoor Speaker", kind: "image", image: bgOutdoorSpeaker.url, css: "" },
  { id: "img-starlight", label: "Starlight Ceiling", kind: "image", image: bgStarlight.url, css: "" },
  { id: "img-network-rack", label: "Network Rack", kind: "image", image: bgNetworkRack.url, css: "" },
  { id: "img-security-cam", label: "Security Camera", kind: "image", image: bgSecurityCam.url, css: "" },
];

export const Route = createFileRoute("/tools/business-cards")({
  head: () => ({
    meta: [
      { title: "Business Card Designer | Apex AV" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: BusinessCards,
});

const SERVICE_OPTIONS: { path: string; label: string }[] = [
  { path: "/services/home-theater", label: "Home Theater" },
  { path: "/services/smart-home", label: "Smart Home" },
  { path: "/services/whole-home-audio", label: "Whole-Home Audio" },
  { path: "/services/whole-home-video", label: "Whole-Home Video" },
  { path: "/services/tv-mounting", label: "TV Mounting" },
  { path: "/services/outdoor", label: "Outdoor AV" },
  { path: "/services/networking", label: "Networking" },
  { path: "/services/security", label: "Security" },
  { path: "/services/lighting", label: "Lighting" },
  { path: "/services/shading", label: "Motorized Shades" },
  { path: "/services/climate-control", label: "Climate Control" },
  { path: "/services/hidden-audio", label: "Hidden Audio" },
  { path: "/services/conference", label: "Conference Rooms" },
  { path: "/services/prewire", label: "New Construction Prewire" },
  { path: "/services/retrofit", label: "Retrofit Wiring" },
];

type CardData = {
  name: string;
  title: string;
  phone: string;
  email: string;
  company: string;
  website: string;
  address: string;
  qrTarget: string;
  services: string[]; // list of labels
  bgFrontId: string;
  bgBackId: string;
  bgFrontOpacity: number; // 0..1 image overlay darken
  bgBackOpacity: number;
  monochrome: boolean;
};

const DEFAULTS: CardData = {
  name: "Your Name",
  title: "Owner / Lead Installer",
  phone: "303-317-3233",
  email: "info@apexav.com",
  company: "Apex AV",
  website: "apexav.com",
  address: "Denver, Colorado",
  qrTarget: "https://apexav.com",
  services: ["Home Theater", "Smart Home", "Networking"],
  bgFrontId: "midnight-indigo",
  bgBackId: "midnight-indigo",
  bgFrontOpacity: 0.55,
  bgBackOpacity: 0.55,
  monochrome: false,
};

function migrate(raw: any): CardData {
  const merged = { ...DEFAULTS, ...(raw || {}) } as CardData & { bgId?: string; bgOpacity?: number };
  if (merged.bgId && !raw?.bgFrontId) merged.bgFrontId = merged.bgId;
  if (merged.bgId && !raw?.bgBackId) merged.bgBackId = merged.bgId;
  if (typeof merged.bgOpacity === "number") {
    if (raw?.bgFrontOpacity == null) merged.bgFrontOpacity = merged.bgOpacity;
    if (raw?.bgBackOpacity == null) merged.bgBackOpacity = merged.bgOpacity;
  }
  delete (merged as any).bgId;
  delete (merged as any).bgOpacity;
  return merged;
}

const STORAGE_KEY = "apex.business-card.v2";

function BusinessCards() {
  const [data, setData] = useState<CardData>(DEFAULTS);
  const [qrDataUrl, setQrDataUrl] = useState<string>("");
  const [previewImages, setPreviewImages] = useState<{ front: string; back: string }>({
    front: "",
    back: "",
  });
  const [exporting, setExporting] = useState<string | null>(null);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) setData(migrate(JSON.parse(raw)));
    } catch {}
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch {}
  }, [data]);

  useEffect(() => {
    let cancelled = false;
    const target = data.qrTarget || `https://${data.website}`;
    QRCode.toDataURL(target, {
      errorCorrectionLevel: "M",
      margin: 0,
      width: 512,
      color: { dark: "#0b0b18", light: "#00000000" },
    })
      .then((url) => {
        if (!cancelled) setQrDataUrl(url);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [data.qrTarget, data.website]);

  const update = (patch: Partial<CardData>) => setData((d) => ({ ...d, ...patch }));

  function toggleService(label: string) {
    setData((d) => {
      const has = d.services.includes(label);
      return { ...d, services: has ? d.services.filter((s) => s !== label) : [...d.services, label] };
    });
  }

  function absoluteUrl(url: string) {
    return new URL(url, window.location.origin).href;
  }

  function blobToDataUrl(blob: Blob) {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ""));
      reader.onerror = () => reject(reader.error || new Error("Could not read image."));
      reader.readAsDataURL(blob);
    });
  }

  async function fetchAsDataUrl(src: string) {
    if (!src || src.startsWith("data:")) return src;
    const res = await fetch(absoluteUrl(src), { cache: "force-cache" });
    if (!res.ok) throw new Error(`Could not load asset: ${src}`);
    return blobToDataUrl(await res.blob());
  }

  async function loadCanvasImage(src: string) {
    const img = new Image();
    img.decoding = "async";
    const loaded = new Promise<HTMLImageElement>((resolve, reject) => {
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error(`Could not render image: ${src}`));
    });
    img.src = await fetchAsDataUrl(src);
    return loaded;
  }

  function drawCover(ctx: CanvasRenderingContext2D, img: HTMLImageElement, x: number, y: number, w: number, h: number) {
    const scale = Math.max(w / img.naturalWidth, h / img.naturalHeight);
    const sw = w / scale;
    const sh = h / scale;
    const sx = (img.naturalWidth - sw) / 2;
    const sy = (img.naturalHeight - sh) / 2;
    ctx.drawImage(img, sx, sy, sw, sh, x, y, w, h);
  }

  function fillRoundedRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
    ctx.fill();
  }

  function drawFitText(
    ctx: CanvasRenderingContext2D,
    text: string,
    x: number,
    y: number,
    maxWidth: number,
    font: string,
    minPx: number,
  ) {
    const sizeMatch = /(\d+(?:\.\d+)?)px/.exec(font);
    const start = sizeMatch ? Number(sizeMatch[1]) : 24;
    let size = start;
    let nextFont = font;
    while (size > minPx && ctx.measureText(text).width > maxWidth) {
      size -= 1;
      nextFont = font.replace(/\d+(?:\.\d+)?px/, `${size}px`);
      ctx.font = nextFont;
    }
    ctx.fillText(text, x, y);
  }

  function wrapTextBySeparator(
    ctx: CanvasRenderingContext2D,
    parts: string[],
    separator: string,
    maxWidth: number,
  ) {
    const lines: string[] = [];
    let current = "";
    parts.forEach((part) => {
      const next = current ? `${current}${separator}${part}` : part;
      if (!current || ctx.measureText(next).width <= maxWidth) {
        current = next;
        return;
      }
      lines.push(current);
      current = part;
    });
    if (current) lines.push(current);
    return lines;
  }

  function getServiceTextLayout(
    ctx: CanvasRenderingContext2D,
    services: string[],
    maxWidth: number,
  ) {
    const count = services.length;
    const preferredSize = count <= 4 ? 24 : count <= 7 ? 21 : count <= 10 ? 18 : 16;
    const parts = services.map((service) => service.toUpperCase());
    for (let size = preferredSize; size >= 11; size -= 1) {
      ctx.font = `500 ${size}px DM Sans, Arial, sans-serif`;
      const lines = wrapTextBySeparator(ctx, parts, " · ", maxWidth);
      if (lines.length <= 4 && lines.every((line) => ctx.measureText(line).width <= maxWidth)) {
        return { lines, size, lineHeight: Math.round(size * 1.35) };
      }
    }
    ctx.font = "500 11px DM Sans, Arial, sans-serif";
    return {
      lines: wrapTextBySeparator(ctx, parts, " · ", maxWidth),
      size: 11,
      lineHeight: 15,
    };
  }

  function drawBackground(ctx: CanvasRenderingContext2D, preset: BgPreset, opacity: number, w: number, h: number) {
    if (preset.kind === "gradient") {
      const colors = preset.css.match(/#[0-9a-f]{6}/gi) || ["#0b0b18", "#14142b", "#1a1440"];
      if (colors.length === 1) {
        ctx.fillStyle = colors[0];
        ctx.fillRect(0, 0, w, h);
        return;
      }
      const grad = ctx.createLinearGradient(0, 0, w, h);
      colors.forEach((color, i) => grad.addColorStop(i / (colors.length - 1), color));
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, w, h);
      const glow = ctx.createRadialGradient(w * 0.9, h * 0.05, 0, w * 0.9, h * 0.05, w * 0.75);
      glow.addColorStop(0, "rgba(77,195,255,0.24)");
      glow.addColorStop(1, "rgba(77,195,255,0)");
      ctx.fillStyle = glow;
      ctx.fillRect(0, 0, w, h);
      return;
    }
    const dark = Math.min(0.95, Math.max(0, opacity));
    const overlay = ctx.createLinearGradient(0, 0, w, h);
    overlay.addColorStop(0, `rgba(5,5,15,${Math.min(0.95, dark + 0.15)})`);
    overlay.addColorStop(0.6, `rgba(5,5,15,${dark})`);
    overlay.addColorStop(1, `rgba(5,5,15,${Math.min(0.95, dark + 0.25)})`);
    ctx.fillStyle = overlay;
    ctx.fillRect(0, 0, w, h);
  }

  function drawGrid(ctx: CanvasRenderingContext2D, w: number, h: number) {
    ctx.save();
    ctx.globalAlpha = 0.18;
    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth = 1;
    for (let x = 0; x <= w; x += 54) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, h);
      ctx.stroke();
    }
    for (let y = 0; y <= h; y += 54) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(w, y);
      ctx.stroke();
    }
    ctx.restore();
  }

  async function renderCardCanvas(side: "front" | "back") {
    if (document.fonts) {
      await document.fonts.ready;
    }
    const w = 1050;
    const h = 600;
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas export is not available in this browser.");
    const preset = BG_PRESETS.find((b) => b.id === (side === "front" ? data.bgFrontId : data.bgBackId)) ?? BG_PRESETS[0];
    if (preset.kind === "image" && preset.image) {
      drawCover(ctx, await loadCanvasImage(preset.image), 0, 0, w, h);
    }
    drawBackground(ctx, preset, side === "front" ? data.bgFrontOpacity : data.bgBackOpacity, w, h);
    
    ctx.fillStyle = "#f5f4ff";
    ctx.textBaseline = "alphabetic";
    if (side === "front") {
      const logoImg = await loadCanvasImage(logo);
      const logoH = 96;
      const logoW = logoImg.naturalWidth * (logoH / logoImg.naturalHeight);
      ctx.drawImage(logoImg, 96, 84, logoW, logoH);
      ctx.fillStyle = "#4dc3ff";
      ctx.beginPath();
      ctx.arc(w - 105, 100, 9, 0, Math.PI * 2);
      ctx.fill();
      ctx.shadowColor = "rgba(77,195,255,0.9)";
      ctx.shadowBlur = 18;
      ctx.fill();
      ctx.shadowBlur = 0;
      ctx.fillStyle = "#f5f4ff";
      ctx.font = "700 72px Space Grotesk, Arial, sans-serif";
      const serviceLayout = data.services.length > 0 ? getServiceTextLayout(ctx, data.services, 858) : null;
      const firstServiceY = serviceLayout
        ? 456 - (serviceLayout.lines.length - 1) * serviceLayout.lineHeight
        : 0;
      const companyY = serviceLayout ? Math.min(408, firstServiceY - 48) : 408;
      drawFitText(ctx, data.company || "Apex AV", 96, companyY, 858, ctx.font, 42);
      if (data.services.length > 0) {
        const layout = serviceLayout ?? getServiceTextLayout(ctx, data.services, 858);
        ctx.fillStyle = "rgba(245,244,255,0.72)";
        ctx.font = `500 ${layout.size}px DM Sans, Arial, sans-serif`;
        layout.lines.forEach((line, index) => {
          ctx.fillText(line, 96, firstServiceY + index * layout.lineHeight);
        });
      }
      const rule = ctx.createLinearGradient(96, 486, 954, 486);
      rule.addColorStop(0, "rgba(139,123,255,0.8)");
      rule.addColorStop(0.65, "rgba(77,195,255,0.2)");
      rule.addColorStop(1, "rgba(77,195,255,0)");
      ctx.fillStyle = rule;
      ctx.fillRect(96, 500, 858, 3);
      ctx.fillStyle = "rgba(245,244,255,0.85)";
      ctx.font = "500 27px DM Sans, Arial, sans-serif";
      ctx.fillText(`${data.website}  ·  ${data.phone}`, 96, 546);
    } else {
      ctx.fillStyle = "#f5f4ff";
      ctx.font = "700 45px Space Grotesk, Arial, sans-serif";
      drawFitText(ctx, data.name || "Your Name", 90, 204, 500, ctx.font, 28);
      ctx.fillStyle = "rgba(245,244,255,0.65)";
      ctx.font = "500 27px DM Sans, Arial, sans-serif";
      drawFitText(ctx, (data.title || "").toUpperCase(), 90, 246, 500, ctx.font, 16);
      const rows = [
        ["P", data.phone],
        ["E", data.email],
        ["W", data.website],
        ["A", data.address],
      ].filter((row) => row[1]);
      rows.forEach((row, i) => {
        const y = 318 + i * 42;
        ctx.fillStyle = "#4dc3ff";
        ctx.font = "700 25px DM Sans, Arial, sans-serif";
        ctx.fillText(row[0], 90, y);
        ctx.fillStyle = "rgba(245,244,255,0.92)";
        ctx.font = "500 28px DM Sans, Arial, sans-serif";
        drawFitText(ctx, row[1], 138, y, 455, ctx.font, 16);
      });
      const qrSize = 345;
      const qrX = w - 90 - qrSize;
      const qrY = 128;
      ctx.fillStyle = "#ffffff";
      fillRoundedRect(ctx, qrX, qrY, qrSize, qrSize, 24);
      if (qrDataUrl) {
        const qrImg = await loadCanvasImage(qrDataUrl);
        ctx.drawImage(qrImg, qrX + 18, qrY + 18, qrSize - 36, qrSize - 36);
      }
      ctx.fillStyle = "rgba(245,244,255,0.7)";
      ctx.font = "500 22px DM Sans, Arial, sans-serif";
      ctx.textAlign = "center";
      ctx.fillText("SCAN", qrX + qrSize / 2, qrY + qrSize + 42);
      ctx.textAlign = "left";
    }
    if (data.monochrome) {
      const img = ctx.getImageData(0, 0, w, h);
      const px = img.data;
      for (let i = 0; i < px.length; i += 4) {
        const g = Math.round(0.299 * px[i] + 0.587 * px[i + 1] + 0.114 * px[i + 2]);
        px[i] = px[i + 1] = px[i + 2] = g;
      }
      ctx.putImageData(img, 0, 0);
    }
    return canvas;
  }

  async function renderCardSheetCanvas() {
    const gap = 90;
    const margin = 75;
    const front = await renderCardCanvas("front");
    const back = await renderCardCanvas("back");
    const canvas = document.createElement("canvas");
    canvas.width = front.width * 2 + gap + margin * 2;
    canvas.height = front.height + margin * 2;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas export is not available in this browser.");
    ctx.fillStyle = "#f7f7fb";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "#20232d";
    ctx.font = "600 24px Arial, sans-serif";
    ctx.fillText("FRONT", margin, 42);
    ctx.fillText("BACK", margin + front.width + gap, 42);
    ctx.drawImage(front, margin, margin);
    ctx.drawImage(back, margin + front.width + gap, margin);
    return canvas;
  }

  function canvasToBlob(canvas: HTMLCanvasElement, type: "image/png" | "image/jpeg", quality = 0.95) {
    return new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (blob) resolve(blob);
          else reject(new Error("Could not create export file."));
        },
        type,
        quality,
      );
    });
  }

  function canvasToDataUrl(canvas: HTMLCanvasElement, type: "image/png" | "image/jpeg" = "image/png") {
    return canvas.toDataURL(type, 0.95);
  }

  function rasterCardToSvg(dataUrl: string) {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="1050" height="600" viewBox="0 0 1050 600"><image href="${dataUrl}" width="1050" height="600" preserveAspectRatio="none"/></svg>`;
    return new Blob([svg], { type: "image/svg+xml;charset=utf-8" });
  }

  function downloadBlob(blob: Blob, filename: string) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 30_000);
  }

  async function download(kind: "png" | "jpg" | "svg", side: "front" | "back" | "both" = "both") {
    setExporting(`${kind.toUpperCase()}${side === "both" ? "" : ` ${side}`}`);
    try {
      const safeName = (data.name || "card").toLowerCase().replace(/[^a-z0-9]+/g, "-");
      const sides: Array<"front" | "back"> = side === "both" ? ["front", "back"] : [side];
      for (const s of sides) {
        const canvas = await renderCardCanvas(s);
        const blob =
          kind === "svg"
            ? rasterCardToSvg(canvasToDataUrl(canvas))
            : await canvasToBlob(canvas, kind === "png" ? "image/png" : "image/jpeg");
        downloadBlob(blob, `apex-card-${safeName}-${s}.${kind}`);
      }
    } catch (err) {
      console.error("[business-cards] export failed", err);
      alert(
        `Export failed: ${err instanceof Error ? err.message : String(err)}\n\nTry PNG or JPG if SVG fails, or use Print / PDF.`,
      );
    } finally {
      setExporting(null);
    }
  }

  async function makePdfBlob() {
    const front = canvasToDataUrl(await renderCardCanvas("front"));
    const back = canvasToDataUrl(await renderCardCanvas("back"));
    const doc = new jsPDF({ orientation: "landscape", unit: "in", format: [3.5, 2] });
    doc.addImage(front, "PNG", 0, 0, 3.5, 2);
    doc.addPage([3.5, 2], "landscape");
    doc.addImage(back, "PNG", 0, 0, 3.5, 2);
    return doc.output("blob");
  }

  async function viewPdf() {
    const popup = window.open("", "_blank");
    setExporting("PDF preview");
    try {
      const blob = await makePdfBlob();
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      if (popup) popup.location.href = url;
      else window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      if (popup) popup.close();
      console.error("[business-cards] pdf preview failed", err);
      alert(`PDF preview failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setExporting(null);
    }
  }

  async function downloadPdf() {
    setExporting("PDF");
    try {
      const blob = await makePdfBlob();
      if (!blob) return;
      const safeName = (data.name || "card").toLowerCase().replace(/[^a-z0-9]+/g, "-");
      downloadBlob(blob, `apex-card-${safeName}.pdf`);
    } catch (err) {
      console.error("[business-cards] pdf export failed", err);
      alert(`PDF export failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setExporting(null);
    }
  }

  async function printCards() {
    const popup = window.open("", "_blank");
    setExporting("Print");
    try {
      const blob = await makePdfBlob();
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      if (popup) {
        popup.document.write(`<!doctype html><title>Print business card</title><style>html,body{margin:0;height:100%;background:#111}iframe{border:0;width:100%;height:100%}.bar{position:fixed;right:16px;top:16px;z-index:2}button{font:600 14px system-ui;padding:10px 14px;border-radius:8px;border:0;background:#fff;color:#111;box-shadow:0 8px 24px rgba(0,0,0,.25);cursor:pointer}</style><div class="bar"><button onclick="document.querySelector('iframe').contentWindow.print()">Print PDF</button></div><iframe src="${url}"></iframe>`);
        popup.document.close();
      } else {
        downloadBlob(blob, "apex-card-print.pdf");
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } finally {
      setExporting(null);
    }
  }

  useEffect(() => {
    let cancelled = false;
    async function renderPreviewImages() {
      try {
        const [frontCanvas, backCanvas] = await Promise.all([
          renderCardCanvas("front"),
          renderCardCanvas("back"),
        ]);
        if (!cancelled) {
          setPreviewImages({
            front: canvasToDataUrl(frontCanvas),
            back: canvasToDataUrl(backCanvas),
          });
        }
      } catch (err) {
        console.error("[business-cards] preview render failed", err);
        if (!cancelled) setPreviewImages({ front: "", back: "" });
      }
    }
    renderPreviewImages();
    return () => {
      cancelled = true;
    };
  }, [data, qrDataUrl]);

  return (
    <div>
      <style>{printCss}</style>

      <div className="no-print">
        <h1 className="font-display text-3xl font-bold text-foreground">Business Card Designer</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Standard US business card (3.5&quot; × 2&quot;). Customize per employee, then print at 100%
          scale or export as PDF/PNG/JPG/SVG.
        </p>

        <div className="mt-8 grid gap-8 lg:grid-cols-[380px_1fr]">
          <div className="space-y-3">
            <Section title="Employee">
              <Field label="Full name" value={data.name} onChange={(v) => update({ name: v })} />
              <Field label="Title" value={data.title} onChange={(v) => update({ title: v })} />
              <Field label="Phone" value={data.phone} onChange={(v) => update({ phone: v })} />
              <Field label="Email" value={data.email} onChange={(v) => update({ email: v })} />
            </Section>

            <Section title="Company">
              <Field label="Company" value={data.company} onChange={(v) => update({ company: v })} />
              <Field label="Website" value={data.website} onChange={(v) => update({ website: v })} />
              <Field label="Address" value={data.address} onChange={(v) => update({ address: v })} />
            </Section>

            <Section title="Services on card">
              <p className="text-xs text-muted-foreground">
                Select the services to feature on the card front.
              </p>
              <div className="grid max-h-56 grid-cols-1 gap-1 overflow-y-auto rounded-md border border-input bg-background p-2 sm:grid-cols-2">
                {SERVICE_OPTIONS.map((s) => {
                  const checked = data.services.includes(s.label);
                  return (
                    <label
                      key={s.path}
                      className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground"
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleService(s.label)}
                        className="h-4 w-4 rounded border-input"
                      />
                      <span className="truncate">{s.label}</span>
                    </label>
                  );
                })}
              </div>
            </Section>

            <Section title="QR code">
              <Field
                label="QR link target"
                value={data.qrTarget}
                onChange={(v) => update({ qrTarget: v })}
              />
              <p className="text-xs text-muted-foreground">
                Tip: use a personalized landing URL per employee (e.g.{" "}
                <code>apexav.com/?ref=chris</code>) for lead attribution.
              </p>
            </Section>

            <BgPicker
              title="Front background"
              value={data.bgFrontId}
              opacity={data.bgFrontOpacity}
              onChange={(id) => update({ bgFrontId: id })}
              onOpacity={(o) => update({ bgFrontOpacity: o })}
            />
            <BgPicker
              title="Back background"
              value={data.bgBackId}
              opacity={data.bgBackOpacity}
              onChange={(id) => update({ bgBackId: id })}
              onOpacity={(o) => update({ bgBackOpacity: o })}
              extraAction={
                <button
                  type="button"
                  className="text-xs text-muted-foreground hover:text-foreground underline"
                  onClick={() =>
                    update({ bgBackId: data.bgFrontId, bgBackOpacity: data.bgFrontOpacity })
                  }
                >
                  Match front
                </button>
              }
            />

            <Section title="Style">
              <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={data.monochrome}
                  onChange={(e) => update({ monochrome: e.target.checked })}
                  className="h-4 w-4"
                />
                Black &amp; white (grayscale)
              </label>
            </Section>




            <div className="flex flex-wrap gap-2 pt-2">
              <Button onClick={printCards} disabled={!!exporting} className="flex-1 min-w-[160px]">
                <Printer className="mr-2 h-4 w-4" /> Print
              </Button>
              <Button variant="outline" onClick={viewPdf} disabled={!!exporting}>
                <Eye className="mr-2 h-4 w-4" /> View PDF
              </Button>
              <Button variant="outline" onClick={downloadPdf} disabled={!!exporting}>
                <Download className="mr-2 h-4 w-4" /> PDF
              </Button>
              <Button variant="outline" onClick={() => download("png", "front")} disabled={!!exporting}>
                <Download className="mr-2 h-4 w-4" /> PNG front
              </Button>
              <Button variant="outline" onClick={() => download("png", "back")} disabled={!!exporting}>
                <Download className="mr-2 h-4 w-4" /> PNG back
              </Button>
              <Button variant="outline" onClick={() => download("jpg", "front")} disabled={!!exporting}>
                <Download className="mr-2 h-4 w-4" /> JPG front
              </Button>
              <Button variant="outline" onClick={() => download("jpg", "back")} disabled={!!exporting}>
                <Download className="mr-2 h-4 w-4" /> JPG back
              </Button>
              <Button variant="outline" onClick={() => download("svg", "front")} disabled={!!exporting}>
                <Download className="mr-2 h-4 w-4" /> SVG front
              </Button>
              <Button variant="outline" onClick={() => download("svg", "back")} disabled={!!exporting}>
                <Download className="mr-2 h-4 w-4" /> SVG back
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setData(DEFAULTS);
                }}
              >
                Reset
              </Button>
            </div>
            {exporting && <p className="text-xs text-muted-foreground">Preparing {exporting}…</p>}
            <PresetsBar current={data} onLoad={setData} />
          </div>

          <div>
            <p className="mb-3 text-xs uppercase tracking-wider text-muted-foreground">Preview</p>
            <div className="flex flex-wrap gap-6">
              <CanvasCardPreview dataUrl={previewImages.front} label="Front card preview" />
              <CanvasCardPreview dataUrl={previewImages.back} label="Back card preview" />
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}

function BgPicker({
  title,
  value,
  opacity,
  onChange,
  onOpacity,
  extraAction,
}: {
  title: string;
  value: string;
  opacity: number;
  onChange: (id: string) => void;
  onOpacity: (o: number) => void;
  extraAction?: ReactNode;
}) {
  const current = BG_PRESETS.find((b) => b.id === value);
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {title}
        </h3>
        {extraAction}
      </div>
      <div className="grid grid-cols-4 gap-2 sm:grid-cols-5">
        {BG_PRESETS.map((b) => {
          const active = value === b.id;
          const style: CSSProperties =
            b.kind === "gradient"
              ? { background: b.css }
              : {
                  backgroundImage: `url(${b.image})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                };
          return (
            <button
              key={b.id}
              type="button"
              onClick={() => onChange(b.id)}
              title={b.label}
              className={`relative h-10 w-full overflow-hidden rounded-md border transition ${
                active ? "border-primary ring-2 ring-primary" : "border-input"
              }`}
              style={style}
            >
              <span className="sr-only">{b.label}</span>
            </button>
          );
        })}
      </div>
      {current?.kind === "image" && (
        <div className="pt-3">
          <Label className="text-xs">Image darken ({Math.round(opacity * 100)}%)</Label>
          <input
            type="range"
            min={0}
            max={90}
            value={Math.round(opacity * 100)}
            onChange={(e) => onOpacity(Number(e.target.value) / 100)}
            className="w-full"
          />
        </div>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
      </h3>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <Input value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function CanvasCardPreview({ dataUrl, label }: { dataUrl: string; label: string }) {
  if (!dataUrl) {
    return <div className="card card-preview-loading" aria-label={label} />;
  }
  return <img src={dataUrl} alt={label} className="card card-preview-image" />;
}

function PresetsBar({ current, onLoad }: { current: CardData; onLoad: (d: CardData) => void }) {
  const KEY = "apex.business-card.presets.v2";
  const [presets, setPresets] = useState<Record<string, CardData>>({});
  useEffect(() => {
    try {
      const raw = localStorage.getItem(KEY);
      if (raw) setPresets(JSON.parse(raw));
    } catch {}
  }, []);
  function save() {
    const name = prompt("Save this card as (employee name):", current.name);
    if (!name) return;
    const next = { ...presets, [name]: current };
    setPresets(next);
    localStorage.setItem(KEY, JSON.stringify(next));
  }
  function remove(name: string) {
    if (!confirm(`Delete preset "${name}"?`)) return;
    const next = { ...presets };
    delete next[name];
    setPresets(next);
    localStorage.setItem(KEY, JSON.stringify(next));
  }
  const names = Object.keys(presets);
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Employee presets
        </h3>
        <Button size="sm" variant="outline" onClick={save}>
          <Download className="mr-2 h-3 w-3" /> Save current
        </Button>
      </div>
      {names.length === 0 ? (
        <p className="mt-3 text-xs text-muted-foreground">
          Save the current card as a preset to quickly switch between employees.
        </p>
      ) : (
        <ul className="mt-3 space-y-1">
          {names.map((n) => (
            <li key={n} className="flex items-center justify-between text-sm">
              <button
                className="text-foreground hover:underline"
                onClick={() => onLoad(presets[n])}
              >
                {n}
              </button>
              <button
                className="text-xs text-muted-foreground hover:text-destructive"
                onClick={() => remove(n)}
              >
                delete
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ---------- Card visuals ---------- */

function BgLayer({ bgId, opacity }: { bgId: string; opacity: number }) {
  const preset = BG_PRESETS.find((b) => b.id === bgId) ?? BG_PRESETS[0];
  if (preset.kind === "gradient") {
    return <div className="card-bg" aria-hidden style={{ background: preset.css }} />;
  }
  return (
    <>
      <img
        className="card-bg card-bg-img"
        aria-hidden
        alt=""
        draggable={false}
        src={preset.image}
      />
      <div
        className="card-bg-overlay"
        aria-hidden
        style={{
          background: `linear-gradient(135deg, rgba(5,5,15,${opacity + 0.15}) 0%, rgba(5,5,15,${opacity}) 60%, rgba(5,5,15,${opacity + 0.25}) 100%)`,
        }}
      />
    </>
  );
}

const CardFront = (() => {
  function CardFrontInner(
    { data }: { data: CardData },
    ref: ForwardedRef<HTMLDivElement>,
  ) {
    const count = data.services.length;
    const svcSize = count <= 4 ? 8 : count <= 7 ? 7 : count <= 10 ? 6.2 : 5.6;
    return (
      <div ref={ref} className="card card-front">
        <BgLayer bgId={data.bgFrontId} opacity={data.bgFrontOpacity} />
        <div className="card-front-inner">
          <div className="card-front-top">
            <img src={logo} alt="Apex AV" className="card-logo" />
            <div className="card-mark-dot" />
          </div>
          <div className="card-front-brand">
            <div className="card-company">{data.company}</div>
            {data.services.length > 0 && (
              <div className="card-services" style={{ fontSize: `${svcSize}px` }}>
                {data.services.join(" · ")}
              </div>
            )}
          </div>
          <div className="card-rule" />
          <div className="card-front-foot">
            <span>{data.website}</span>
            <span>·</span>
            <span>{data.phone}</span>
          </div>
        </div>
      </div>
    );
  }
  return forwardRef(CardFrontInner) as ForwardRefExoticComponent<
    { data: CardData } & RefAttributes<HTMLDivElement>
  >;
})();

const CardBack = (() => {
  function CardBackInner(
    { data, qrDataUrl }: { data: CardData; qrDataUrl: string },
    ref: ForwardedRef<HTMLDivElement>,
  ) {
    return (
      <div ref={ref} className="card card-back">
        <BgLayer bgId={data.bgBackId} opacity={data.bgBackOpacity} />
        <div className="card-back-inner">
          <div className="card-back-left">
            <div className="card-name">{data.name}</div>
            <div className="card-title">{data.title}</div>

            <div className="card-details">
              <Row label="P" value={data.phone} />
              <Row label="E" value={data.email} />
              <Row label="W" value={data.website} />
              <Row label="A" value={data.address} />
            </div>
          </div>
          <div className="card-back-right">
            {qrDataUrl ? (
              <img src={qrDataUrl} alt="QR code" className="card-qr" />
            ) : (
              <div className="card-qr card-qr-placeholder" />
            )}
            <div className="card-qr-caption">Scan</div>
          </div>
        </div>
      </div>
    );
  }
  return forwardRef(CardBackInner) as ForwardRefExoticComponent<
    { data: CardData; qrDataUrl: string } & RefAttributes<HTMLDivElement>
  >;
})();

function Row({ label, value }: { label: string; value: string }) {
  if (!value) return null;
  return (
    <div className="card-row">
      <span className="card-row-label">{label}</span>
      <span className="card-row-value">{value}</span>
    </div>
  );
}

/* ---------- CSS ---------- */

const printCss = `
.card {
  position: relative;
  width: 3.5in;
  height: 2in;
  border-radius: 10px;
  overflow: hidden;
  color: #f5f4ff;
  font-family: 'DM Sans', system-ui, -apple-system, sans-serif;
  box-shadow: 0 20px 40px -20px rgba(10, 10, 30, 0.4), 0 2px 6px rgba(10, 10, 30, 0.15);
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

.card-bg,
.card-bg-overlay {
  position: absolute;
  inset: 0;
}
.card-bg-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  pointer-events: none;
  user-select: none;
}

/* FRONT */
.card-front-inner {
  position: relative;
  height: 100%;
  padding: 0.28in 0.32in;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}
.card-front-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.card-logo {
  height: 32px;
  width: auto;
  object-fit: contain;
  filter: drop-shadow(0 2px 8px rgba(77,195,255,0.35));
}
.card-mark-dot {
  width: 6px;
  height: 6px;
  border-radius: 999px;
  background: #4dc3ff;
  box-shadow: 0 0 12px #4dc3ff;
}
.card-front-brand {
  margin-top: auto;
}
.card-company {
  font-family: 'Space Grotesk', system-ui, sans-serif;
  font-weight: 700;
  font-size: 24px;
  letter-spacing: -0.02em;
  line-height: 1;
}
.card-services {
  margin-top: 6px;
  font-size: 8px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(245, 244, 255, 0.72);
  line-height: 1.35;
}
.card-rule {
  margin: 8px 0 6px;
  height: 1px;
  background: linear-gradient(90deg, rgba(139,123,255,0.8), rgba(77,195,255,0.2) 60%, transparent);
}
.card-front-foot {
  display: flex;
  gap: 8px;
  font-size: 9px;
  color: rgba(245, 244, 255, 0.85);
  letter-spacing: 0.04em;
}

/* BACK */
.card-back-inner {
  position: relative;
  height: 100%;
  padding: 0.28in 0.3in;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 14px;
  align-items: stretch;
}
.card-back-left {
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
}
.card-name {
  font-family: 'Space Grotesk', system-ui, sans-serif;
  font-weight: 700;
  font-size: 15px;
  letter-spacing: -0.01em;
  line-height: 1.1;
}
.card-title {
  margin-top: 3px;
  font-size: 9px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(245, 244, 255, 0.65);
}
.card-details {
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.card-row {
  display: flex;
  align-items: baseline;
  gap: 8px;
  font-size: 9.5px;
  color: rgba(245, 244, 255, 0.92);
  min-width: 0;
}
.card-row-label {
  flex: none;
  width: 10px;
  color: #4dc3ff;
  font-weight: 700;
  letter-spacing: 0.1em;
  font-size: 8.5px;
}
.card-row-value {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.card-back-right {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
}
.card-qr {
  width: 1.15in;
  height: 1.15in;
  padding: 6px;
  background: #ffffff;
  border-radius: 8px;
}
.card-qr-placeholder {
  background: rgba(255,255,255,0.1);
}
.card-qr-caption {
  font-size: 7.5px;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: rgba(245, 244, 255, 0.7);
}

`;
